import React from "react";
import { useRouter } from "next/router";
import StepLayout from "./StepLayout";
import OptionList from "./OptionList";
import StepActions from "./StepActions";
import OtherRoleChooser from "./OtherRoleChooser";
import {
  DISCOVERY_SUBTITLE,
  DISCOVERY_TITLE_1,
  DISCOVERY_TITLE_2,
  getInterestOptions,
} from "@components/Discovery/config";
import InterestSelection from "@components/Discovery/InterestSelection";

// Helper function to get interest option by sub-role and selection ID
const getInterestOptionBySubRoleAndId = (subRole, selectionId) => {
  const options = getInterestOptions(subRole);
  return options.find((option) => option.id === selectionId);
};

/**
 * DiscoveryFlow - generic multi-step flow that loads steps from config.
 *
 * props:
 * - config: {
 *     roles: [{ id, label }],
 *     optionWithRoles: { [audienceId]: [{ id, title, subtitle?, options: [{id,label,description?}] }] },
 *     resolveLink: (audienceId, answers) => string
 *   }
 */
export default function DiscoveryFlow({ config }) {
  const router = useRouter();
  const [role, setRole] = React.useState(null);
  const [pendingRole, setPendingRole] = React.useState(null);
  const [answers, setAnswers] = React.useState({});
  const [otherSubRole, setOtherSubRole] = React.useState(null);
  const [otherDescription, setOtherDescription] = React.useState("");
  const [isOtherExpanded, setIsOtherExpanded] = React.useState(false);
  const [showInterestSelection, setShowInterestSelection] =
    React.useState(false);
  const [interestSelection, setInterestSelection] = React.useState(null);

  const onReset = () => {
    setRole(null);
    setPendingRole(null);
    setAnswers({});
    setIsOtherExpanded(false);
    setShowInterestSelection(false);
    setInterestSelection(null);
  };

  const handleRoleChange = (roleId) => {
    if (roleId === "other") {
      if (pendingRole === "other") {
        // If "other" is already selected, toggle expand/collapse
        setIsOtherExpanded(!isOtherExpanded);
      } else {
        // If selecting "other" for the first time, select it and expand
        setPendingRole(roleId);
        setIsOtherExpanded(true);
      }
    } else {
      // For non-other roles, just set the role and collapse other
      setPendingRole(roleId);
      setIsOtherExpanded(false);
    }
  };

  // Audience selection screen
  if (!role) {
    const onNextAudience = () => {
      if (pendingRole) {
        // If Other is chosen and expanded, require a sub-role
        if (pendingRole === "other" && isOtherExpanded && !otherSubRole) {
          return;
        }
        // Persist detail for downstream use
        if (pendingRole === "other") {
          setAnswers((prev) => ({
            ...prev,
            role_detail: {
              subRole: otherSubRole,
              description: otherDescription,
            },
          }));

          // Check if this sub-role needs the two-step process
          const twoStepSubRoles = ["hs_counselor", "college_admin", "cbo"];
          if (twoStepSubRoles.includes(otherSubRole)) {
            setShowInterestSelection(true);
            setRole(pendingRole);
            return;
          }
        }
        setRole(pendingRole);
      }
    };
    const onBackAudience = () => {
      router.push(process.env.NEXT_PUBLIC_WORD_PRESS_URL);
    };
    return (
      <StepLayout
        title={DISCOVERY_TITLE_1}
        subtitle={DISCOVERY_SUBTITLE}
        onBack={onBackAudience}
        footer={
          <StepActions
            onNext={onNextAudience}
            nextDisabled={
              !pendingRole ||
              (pendingRole === "other" && isOtherExpanded && !otherSubRole)
            }
            nextLabel="Next"
          />
        }
      >
        <OptionList
          options={config.roles}
          value={pendingRole}
          onChange={handleRoleChange}
          expandedOptionId={isOtherExpanded ? "other" : null}
        />
        {pendingRole === "other" && isOtherExpanded && (
          <OtherRoleChooser
            value={otherSubRole}
            onChange={setOtherSubRole}
            description={otherDescription}
            onChangeDescription={setOtherDescription}
          />
        )}
      </StepLayout>
    );
  }

  // Interest selection screen for specific sub-roles
  if (role === "other" && showInterestSelection) {
    const subRole = answers.role_detail?.subRole;
    const onNextInterest = () => {
      if (!interestSelection) {
        return;
      }
      // Navigate directly to the selected interest's href
      const selectedOption = getInterestOptionBySubRoleAndId(
        subRole,
        interestSelection,
      );
      if (selectedOption?.href) {
        router.push(selectedOption.href);
      }
    };

    const onBackInterest = () => {
      setShowInterestSelection(false);
      setRole(null);
      setInterestSelection(null);
    };

    return (
      <StepLayout
        title={DISCOVERY_TITLE_2}
        subtitle={DISCOVERY_SUBTITLE}
        onBack={onBackInterest}
        footer={
          <StepActions
            onNext={onNextInterest}
            nextDisabled={!interestSelection}
            nextLabel="Next"
          />
        }
      >
        <InterestSelection
          subRole={subRole}
          value={interestSelection}
          onChange={setInterestSelection}
        />
      </StepLayout>
    );
  }

  const roleDefinition = config.optionWithRoles[role];
  const currentStep = roleDefinition; // single-step per role
  const stepId = currentStep?.id ?? "single";
  const currentValue = answers[stepId] ?? null;

  const handleChange = (val) => {
    setAnswers((prev) => ({ ...prev, [stepId]: val }));
  };

  const goBack = () => {
    onReset();
  };

  const goNext = () => {
    if (!currentValue) {
      return;
    }
    // Single step per role -> navigate based on selection
    const selectedId = currentValue;
    const opts = currentStep?.options || [];
    const selectedOpt = opts.find((opt) => opt.id === selectedId);
    const target = selectedOpt?.href;
    if (target) {
      router.push(target);
    }
  };

  return (
    <StepLayout
      title={currentStep?.title}
      subtitle={currentStep?.subtitle}
      onBack={goBack}
      footer={<StepActions onNext={goNext} nextDisabled={!currentValue} />}
    >
      <OptionList
        options={currentStep?.options || []}
        value={currentValue}
        onChange={handleChange}
      />
    </StepLayout>
  );
}
